import { BusinessObject } from './business-object.js'
import { assignCommonProperties } from '../util/data/assign-common-properties.js'

import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
	jsonMapProperty,
} from '@sumbricht/json-class-serializer'
import { BoReference } from './bo-reference.js'

export const ThemeTypes = ['light', 'dark'] as const
export type ThemeType = (typeof ThemeTypes)[number]
export const ThemeTypographyLevelNames = [
	'headline-1',
	'headline-2',
	'headline-3',
	'headline-4',
	'headline-5',
	'headline-6',
	'subtitle-1',
	'body-1',
	'body-2',
	'subtitle-2',
	'caption',
] as const
export type ThemeTypographyLevelName =
	(typeof ThemeTypographyLevelNames)[number]

@jsonClass('ThemePalette')
export class ThemePalette {
	@jsonProperty(String) paletteName?: string
	@jsonProperty(String) defaultHue?: string
	@jsonProperty(String) lighterHue?: string
	@jsonProperty(String) darkerHue?: string
	@jsonProperty(String) textHue?: string

	constructor(init?: Partial<ThemePalette>) {
		assignCommonProperties(this, init)
	}

	getDefinePalette() {
		const defaultHue = this.defaultHue || '500'
		const lighterHue = this.lighterHue || '100'
		const darkerHue = this.darkerHue || '700'
		const textHue = this.textHue || defaultHue

		return `mat.m2-define-palette(mat.$m2-${this.paletteName}-palette, ${defaultHue}, ${lighterHue}, ${darkerHue}, ${textHue})`
	}
}

@jsonClass('ThemeTypographyLevel')
export class ThemeTypographyLevel {
	@jsonProperty(String) fontFamily: string = 'sans-serif'
	@jsonProperty(Number) fontWeight: number = 400
	@jsonProperty(String) fontSize: string = '1rem'
	@jsonProperty(Number) lineHeight: number = 1
	@jsonProperty(String) letterSpacing: string = 'normal'
}

@jsonClass('ThemeTypography')
export class ThemeTypography {
	@jsonMapProperty(String, ThemeTypographyLevel)
	levels: Map<ThemeTypographyLevelName, ThemeTypographyLevel> = new Map()
}

@jsonClass('Theme')
export class Theme extends BusinessObject {
	@jsonProperty(BoReference) extendsThemeRef?: BoReference
	@jsonProperty(String) type: ThemeType = 'light'

	@jsonProperty(ThemePalette) primaryPalette: ThemePalette = new ThemePalette({
		paletteName: 'indigo',
		defaultHue: '500',
	})
	@jsonProperty(ThemePalette) accentPalette: ThemePalette = new ThemePalette({
		paletteName: 'pink',
		defaultHue: 'A200',
		lighterHue: 'A100',
		darkerHue: 'A400',
	})
	@jsonProperty(ThemePalette) warnPalette: ThemePalette = new ThemePalette({
		paletteName: 'red',
	})

	@jsonProperty(ThemeTypography) typography: ThemeTypography =
		new ThemeTypography()

	@jsonProperty(Number) density: number = 0

	constructor(init?: Partial<Theme>) {
		super()
		assignCommonProperties(this, init)
	}

	getPrefixName() {
		return `${this.moduleId}__${this.boId}`
	}

	getThemeClassName() {
		return `${this.getPrefixName()}-theme`
	}
}
